import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { 
  Clock, Calendar as CalendarIcon, Sun, Coffee, LogOut, AlertTriangle, 
  Plane, MapPin, Check, X, ChevronLeft, ChevronRight, Send, History,
  Stethoscope, User, BarChart3
} from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isToday, isSameDay, getDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { es } from 'date-fns/locale';

const MADRID_TZ = 'Europe/Madrid';

const API = process.env.REACT_APP_BACKEND_URL;

// API base with /api prefix for all backend calls
const API_BASE = `${API}/api`;

const ENTRY_TYPES = [
  { value: 'entrada', label: 'Entrada', icon: Sun, color: 'bg-green-500 hover:bg-green-600' },
  { value: 'salida_comer', label: 'Salida a comer', icon: Coffee, color: 'bg-yellow-500 hover:bg-yellow-600' },
  { value: 'entrada_comer', label: 'Vuelta de comer', icon: Coffee, color: 'bg-blue-500 hover:bg-blue-600' },
  { value: 'salida_medico', label: 'Salida Médico', icon: Stethoscope, color: 'bg-pink-500 hover:bg-pink-600' },
  { value: 'entrada_medico', label: 'Entrada Médico', icon: Stethoscope, color: 'bg-pink-400 hover:bg-pink-500' },
  { value: 'salida_personal', label: 'Salida Asuntos', icon: User, color: 'bg-orange-500 hover:bg-orange-600' },
  { value: 'entrada_personal', label: 'Entrada Asuntos', icon: User, color: 'bg-orange-400 hover:bg-orange-500' },
  { value: 'salida_incidencia', label: 'Salida Incidencia', icon: AlertTriangle, color: 'bg-fuchsia-500 hover:bg-fuchsia-600' },
  { value: 'entrada_incidencia', label: 'Entrada Incidencia', icon: AlertTriangle, color: 'bg-fuchsia-400 hover:bg-fuchsia-500' },
  { value: 'salida', label: 'Fin de jornada', icon: LogOut, color: 'bg-red-500 hover:bg-red-600' },
];

const ENTRY_BADGES = {
  'entrada': 'bg-green-100 text-green-800',
  'salida_comer': 'bg-yellow-100 text-yellow-800',
  'entrada_comer': 'bg-blue-100 text-blue-800',
  'salida_medico': 'bg-pink-100 text-pink-800',
  'entrada_medico': 'bg-pink-100 text-pink-800',
  'salida_personal': 'bg-orange-100 text-orange-800',
  'entrada_personal': 'bg-orange-100 text-orange-800',
  'salida_incidencia': 'bg-fuchsia-100 text-fuchsia-800',
  'entrada_incidencia': 'bg-fuchsia-100 text-fuchsia-800',
  'salida': 'bg-red-100 text-red-800',
  'incidencia': 'bg-fuchsia-100 text-fuchsia-800',
};

const COMUNIDADES = [
  "Andalucía", "Aragón", "Asturias", "Baleares", "Canarias", "Cantabria",
  "Castilla-La Mancha", "Castilla y León", "Cataluña", "Comunidad Valenciana",
  "Extremadura", "Galicia", "La Rioja", "Madrid", "Murcia", "Navarra", "País Vasco", "Ceuta", "Melilla"
];

const WorkerTimeTracking = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  
  const [activeTab, setActiveTab] = useState('fichar');
  const [loading, setLoading] = useState(false);
  
  // Today's entries
  const [todayEntries, setTodayEntries] = useState([]);
  const [clockedTypes, setClockedTypes] = useState(new Set());
  const journeyEnded = clockedTypes.has('salida');
  
  // Incidencia
  const [showIncidencia, setShowIncidencia] = useState(false);
  const [incidenciaNotes, setIncidenciaNotes] = useState('');
  const [incidenciaType, setIncidenciaType] = useState('medico');
  // Motivo requerido cuando el trabajador ficha "Salida Incidencia"
  const [pendingIncidenciaEntry, setPendingIncidenciaEntry] = useState(null);
  const [incidenciaReason, setIncidenciaReason] = useState('');
  
  // Vacation
  const [vacation, setVacation] = useState(null);
  const [vacationDays, setVacationDays] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [vacationMonth, setVacationMonth] = useState(new Date());
  const [vacationNotes, setVacationNotes] = useState('');
  
  // Holidays
  const [holidays, setHolidays] = useState([]);
  const [comunidad, setComunidad] = useState('');
  const [holidayMonth, setHolidayMonth] = useState(new Date());
  
  // History
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyMonth, setHistoryMonth] = useState(new Date());
  const [historyTotal, setHistoryTotal] = useState(0);
  
  // Schedule
  // ...state preserved below...

  // Nov 2026 — self-service summary (horas día/semana/mes/año + vacaciones + festivos)
  const [summary, setSummary] = useState(null);
  const [schedule, setSchedule] = useState(null);
  
  // Fetch today's entries
  const fetchTodayEntries = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/worker/time-tracking/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayEntries(response.data.entries);
      setClockedTypes(new Set(response.data.entries.map(e => e.entry_type)));
    } catch (error) {
      console.error('Error fetching today entries:', error);
    }
  }, [token]);
  
  // Fetch vacations
  const fetchVacations = useCallback(async () => {
    try {
      const year = vacationMonth.getFullYear();
      const response = await axios.get(`${API_BASE}/worker/time-tracking/my-vacations?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVacation(response.data.vacation);
      setVacationDays(response.data.days);
    } catch (error) {
      console.error('Error fetching vacations:', error);
    }
  }, [token, vacationMonth]);
  
  // Fetch holidays
  const fetchHolidays = useCallback(async () => {
    try {
      const year = new Date().getFullYear();
      const response = await axios.get(`${API_BASE}/worker/time-tracking/holidays?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(response.data.holidays);
      setComunidad(response.data.comunidad || '');
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  }, [token]);
  
  // Fetch schedule
  const fetchSchedule = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/worker/time-tracking/my-schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedule(response.data.schedule);
      setComunidad(response.data.schedule?.comunidad || '');
    } catch (error) {
      console.error('Error fetching schedule:', error);
    }
  }, [token]);
  
  // Fetch history entries
  const fetchHistory = useCallback(async () => {
    try {
      const year = historyMonth.getFullYear();
      const month = historyMonth.getMonth() + 1;
      const dateFrom = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const dateTo = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
      
      const response = await axios.get(`${API_BASE}/worker/time-tracking/my-entries`, {
        params: { date_from: dateFrom, date_to: dateTo, limit: 200 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoryEntries(response.data.entries || []);
      setHistoryTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  }, [token, historyMonth]);
  
  const fetchSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/worker/time-tracking/summary`, { headers: { Authorization: `Bearer ${token}` } });
      setSummary(r.data);
    } catch (e) {
      console.error('summary fetch failed', e);
    }
  }, [token]);

  useEffect(() => {
    fetchTodayEntries();
    fetchSchedule();
    fetchSummary();  // banner needs 'today' hours even on the Fichar tab
  }, [fetchTodayEntries, fetchSchedule, fetchSummary]);

  // Auto-refresh today's entries every 60s so the UI resets automatically
  // at midnight (Madrid) once the backend starts reporting a new civil date.
  useEffect(() => {
    const id = setInterval(() => { fetchTodayEntries(); fetchSummary(); }, 60000);
    return () => clearInterval(id);
  }, [fetchTodayEntries, fetchSummary]);
  
  useEffect(() => {
    if (activeTab === 'vacaciones') fetchVacations();
    if (activeTab === 'festivos') fetchHolidays();
    if (activeTab === 'historial') fetchHistory();
    if (activeTab === 'resumen') fetchSummary();
  }, [activeTab, fetchVacations, fetchHolidays, fetchHistory, fetchSummary]);
  
  // Clock in/out — if user selects `salida_incidencia`, prompt for reason first.
  const handleClock = async (entryType, extraNotes = '') => {
    if (entryType === 'salida_incidencia' && !extraNotes) {
      // Open the reason modal; the actual clock happens when user confirms.
      setPendingIncidenciaEntry(entryType);
      setIncidenciaReason('');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/worker/time-tracking/clock`, {
        entry_type: entryType,
        notes: extraNotes || '',
        incidencia_type: entryType === 'salida_incidencia' ? 'incidencia' : ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${ENTRY_TYPES.find(t => t.value === entryType)?.label || entryType} registrada`);
      fetchTodayEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al fichar');
    } finally {
      setLoading(false);
    }
  };

  const submitIncidenciaReason = async () => {
    const reason = incidenciaReason.trim();
    if (!reason) {
      toast.error('Describe el motivo de la salida');
      return;
    }
    const entryType = pendingIncidenciaEntry;
    setPendingIncidenciaEntry(null);
    await handleClock(entryType, reason);
  };
  
  // Report incidencia
  const handleIncidencia = async () => {
    if (journeyEnded) {
      toast.error('Jornada finalizada. No puedes registrar incidencias hasta mañana.');
      return;
    }
    if (!incidenciaNotes.trim()) {
      toast.error('Describe la incidencia');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/worker/time-tracking/clock`, {
        entry_type: 'incidencia',
        notes: incidenciaNotes,
        incidencia_type: incidenciaType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Incidencia registrada');
      setShowIncidencia(false);
      setIncidenciaNotes('');
      fetchTodayEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar incidencia');
    } finally {
      setLoading(false);
    }
  };
  
  // Request vacation
  const handleRequestVacation = async () => {
    if (selectedDates.length === 0) {
      toast.error('Selecciona al menos una fecha');
      return;
    }
    
    setLoading(true);
    try {
      const dates = selectedDates.map(d => format(d, 'yyyy-MM-dd'));
      await axios.post(`${API_BASE}/worker/time-tracking/request-vacation`, {
        dates,
        notes: vacationNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${dates.length} día(s) de vacaciones solicitados. Pendiente de aprobación.`);
      setSelectedDates([]);
      setVacationNotes('');
      fetchVacations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al solicitar vacaciones');
    } finally {
      setLoading(false);
    }
  };
  
  // Update comunidad
  const handleUpdateComunidad = async (newComunidad) => {
    try {
      await axios.put(`${API_BASE}/worker/time-tracking/my-schedule`, {
        comunidad: newComunidad
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComunidad(newComunidad);
      toast.success('Comunidad actualizada');
      fetchHolidays();
    } catch (error) {
      toast.error('Error al actualizar comunidad');
    }
  };
  
  // Calendar helpers
  const monthStart = startOfMonth(vacationMonth);
  const monthEnd = endOfMonth(vacationMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  const isVacationDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return vacationDays.find(d => d.date === dateStr);
  };
  
  const isHolidayDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return holidays.some(h => h.date === dateStr);
  };
  
  const getEntryLabel = (type) => {
    const entry = ENTRY_TYPES.find(t => t.value === type);
    return entry?.label || type;
  };

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold flex items-center justify-center gap-2">
          <Clock className="h-6 w-6 text-primary" />
          Mi Fichaje
        </h1>
        <p className="text-muted-foreground">Hola, {user?.name || user?.email}</p>
      </div>

      {/* Nov 2026 — banner con horas de HOY visible desde cualquier tab */}
      {summary && (
        <Card data-testid="worker-summary-banner" className="bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-primary/20">
          <CardContent className="py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Hoy</div>
                <div className="text-2xl font-bold text-primary">
                  {summary.today.hours}h {summary.today.minutes}m
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Esta semana</div>
                <div className="text-2xl font-bold">
                  {summary.this_week.hours}h {summary.this_week.minutes}m
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Este mes</div>
                <div className="text-2xl font-bold">
                  {summary.this_month.hours}h {summary.this_month.minutes}m
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Vacaciones</div>
                <div className="text-2xl font-bold text-emerald-600">
                  {summary.vacations.available}
                  <span className="text-sm text-muted-foreground font-normal"> / {summary.vacations.total}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="resumen" className="flex items-center gap-2" data-testid="worker-tab-resumen">
            <BarChart3 className="h-4 w-4" />
            Resumen
          </TabsTrigger>
          <TabsTrigger value="fichar" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Fichar
          </TabsTrigger>
          <TabsTrigger value="historial" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Historial
          </TabsTrigger>
          <TabsTrigger value="vacaciones" className="flex items-center gap-2">
            <Plane className="h-4 w-4" />
            Vacaciones
          </TabsTrigger>
          <TabsTrigger value="festivos" className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4" />
            Festivos
          </TabsTrigger>
        </TabsList>

        {/* ─── Resumen (read-only) ─────────────────────────────────── */}
        <TabsContent value="resumen" className="space-y-4">
          {!summary ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground">Cargando resumen...</CardContent></Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card data-testid="summary-today">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Horas HOY</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-primary">{summary.today.hours}h {summary.today.minutes}m</div>
                    <p className="text-xs text-muted-foreground mt-1">{summary.reference_date}</p>
                  </CardContent>
                </Card>
                <Card data-testid="summary-week">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Esta SEMANA</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{summary.this_week.hours}h {summary.this_week.minutes}m</div>
                    <p className="text-xs text-muted-foreground mt-1">{summary.this_week.start} → {summary.this_week.end}</p>
                  </CardContent>
                </Card>
                <Card data-testid="summary-month">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Este MES</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{summary.this_month.hours}h {summary.this_month.minutes}m</div>
                    <p className="text-xs text-muted-foreground mt-1">{summary.this_month.start} → {summary.this_month.end}</p>
                  </CardContent>
                </Card>
                <Card data-testid="summary-year">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Este AÑO</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">{summary.this_year.hours}h {summary.this_year.minutes}m</div>
                    <p className="text-xs text-muted-foreground mt-1">{summary.this_year.start} → {summary.this_year.end}</p>
                  </CardContent>
                </Card>
              </div>

              <Card data-testid="summary-vacations">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plane className="h-5 w-5 text-primary" />
                    Vacaciones {summary.vacations.year}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div className="rounded-lg border p-3">
                      <div className="text-xs text-muted-foreground uppercase">Total</div>
                      <div className="text-2xl font-bold">{summary.vacations.total}</div>
                    </div>
                    <div className="rounded-lg border p-3 bg-emerald-50 dark:bg-emerald-900/10">
                      <div className="text-xs text-emerald-700 dark:text-emerald-300 uppercase">Disponibles</div>
                      <div className="text-2xl font-bold text-emerald-600">{summary.vacations.available}</div>
                    </div>
                    <div className="rounded-lg border p-3 bg-amber-50 dark:bg-amber-900/10">
                      <div className="text-xs text-amber-700 dark:text-amber-300 uppercase">Pendientes</div>
                      <div className="text-2xl font-bold text-amber-600">{summary.vacations.pending}</div>
                    </div>
                    <div className="rounded-lg border p-3 bg-slate-50 dark:bg-slate-800/40">
                      <div className="text-xs text-muted-foreground uppercase">Usados</div>
                      <div className="text-2xl font-bold">{summary.vacations.used}</div>
                    </div>
                  </div>
                  {summary.vacations.comunidad && (
                    <p className="text-xs text-muted-foreground text-center mt-3">
                      Comunidad: {summary.vacations.comunidad}
                    </p>
                  )}
                </CardContent>
              </Card>

              {summary.upcoming_holidays?.length > 0 && (
                <Card data-testid="summary-holidays">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CalendarIcon className="h-5 w-5 text-primary" />
                      Próximos festivos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="divide-y">
                      {summary.upcoming_holidays.map((h, i) => (
                        <li key={i} className="py-2 flex items-center justify-between">
                          <div>
                            <div className="font-medium">{h.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {h.scope === 'nacional' ? 'Nacional' : `Local — ${h.comunidad || ''}`}
                            </div>
                          </div>
                          <div className="text-sm font-mono">{h.date}</div>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              <p className="text-xs text-muted-foreground text-center italic">
                Esta vista es sólo lectura. Para fichar usa la pestaña "Fichar" · para solicitar vacaciones usa la pestaña "Vacaciones".
              </p>
            </>
          )}
        </TabsContent>
        
        {/* FICHAR TAB */}
        <TabsContent value="fichar" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Fichaje del día</CardTitle>
              <CardDescription>{format(new Date(), 'EEEE, d MMMM yyyy', { locale: es })}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Journey ended banner */}
              {journeyEnded && (
                <div
                  className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800"
                  data-testid="journey-ended-banner"
                >
                  <LogOut className="h-5 w-5 shrink-0" />
                  <div className="text-sm">
                    <p className="font-semibold">Jornada finalizada</p>
                    <p>Ya has fichado &quot;Fin de jornada&quot;. Los fichajes se reactivarán mañana automáticamente.</p>
                  </div>
                </div>
              )}
              {/* Clock buttons - main entries */}
              <div className="grid grid-cols-2 gap-3">
                {ENTRY_TYPES.filter(t => ['entrada', 'salida_comer', 'entrada_comer', 'salida'].includes(t.value)).map(type => {
                  const Icon = type.icon;
                  const isClocked = clockedTypes.has(type.value);
                  const isDisabled = loading || isClocked || (journeyEnded && type.value !== 'salida');

                  return (
                    <Button
                      key={type.value}
                      onClick={() => handleClock(type.value)}
                      disabled={isDisabled}
                      className={`h-16 text-base ${(isClocked || (journeyEnded && type.value !== 'salida')) ? 'bg-gray-300' : type.color} text-white`}
                      data-testid={`clock-btn-${type.value}`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <Icon className="h-5 w-5" />
                        <span className="text-sm">{type.label}</span>
                        {isClocked && <Check className="h-4 w-4" />}
                      </div>
                    </Button>
                  );
                })}
              </div>
              
              {/* Clock buttons - special entries (médico, asuntos, incidencia) */}
              <div className="grid grid-cols-2 gap-3">
                {ENTRY_TYPES.filter(t => ['salida_medico', 'entrada_medico', 'salida_personal', 'entrada_personal', 'salida_incidencia', 'entrada_incidencia'].includes(t.value)).map(type => {
                  const Icon = type.icon;
                  const isClocked = clockedTypes.has(type.value);
                  const isDisabled = loading || isClocked || journeyEnded;

                  return (
                    <Button
                      key={type.value}
                      onClick={() => handleClock(type.value)}
                      disabled={isDisabled}
                      className={`h-14 text-sm ${(isClocked || journeyEnded) ? 'bg-gray-300' : type.color} text-white`}
                      data-testid={`clock-btn-${type.value}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        <span>{type.label}</span>
                        {isClocked && <Check className="h-3 w-3" />}
                      </div>
                    </Button>
                  );
                })}
              </div>
              
              {/* Incidencia button */}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowIncidencia(!showIncidencia)}
                disabled={journeyEnded}
                data-testid="report-incidencia-toggle"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Reportar Incidencia
              </Button>
              
              {showIncidencia && (
                <div className="p-4 bg-purple-50 rounded-lg space-y-3">
                  <Select value={incidenciaType} onValueChange={setIncidenciaType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="medico">Médico</SelectItem>
                      <SelectItem value="personal">Personal</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea
                    placeholder="Describe la incidencia..."
                    value={incidenciaNotes}
                    onChange={(e) => setIncidenciaNotes(e.target.value)}
                  />
                  <Button onClick={handleIncidencia} disabled={loading} className="w-full">
                    <Send className="h-4 w-4 mr-2" />
                    Enviar Incidencia
                  </Button>
                </div>
              )}
              
              {/* Today's entries */}
              {todayEntries.length > 0 && (
                <div className="pt-4 border-t">
                  <h3 className="font-medium mb-3">Fichajes de hoy</h3>
                  <div className="space-y-2">
                    {todayEntries.map(entry => (
                      <div key={entry.id} className="flex items-start justify-between p-2 bg-gray-50 rounded">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={ENTRY_BADGES[entry.entry_type]}>
                              {getEntryLabel(entry.entry_type)}
                            </Badge>
                            {entry.incidencia_type && entry.incidencia_type !== 'incidencia' && (
                              <span className="text-xs text-muted-foreground">({entry.incidencia_type})</span>
                            )}
                          </div>
                          {entry.notes && (
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2" title={entry.notes}>
                              Motivo: {entry.notes}
                            </p>
                          )}
                        </div>
                        <span className="font-mono text-sm shrink-0 ml-3">
                          {formatInTimeZone(parseISO(entry.timestamp), MADRID_TZ, 'HH:mm')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* HISTORIAL TAB */}
        <TabsContent value="historial" className="space-y-4">
          {/* Calendar view */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Calendario de Fichajes</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setHistoryMonth(new Date(historyMonth.setMonth(historyMonth.getMonth() - 1)))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="font-medium min-w-[140px] text-center">
                    {format(historyMonth, 'MMMM yyyy', { locale: es })}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => setHistoryMonth(new Date(historyMonth.setMonth(historyMonth.getMonth() + 1)))}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <CardDescription>
                {historyTotal} fichajes en este período
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1 mb-4">
                {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(day => (
                  <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                    {day}
                  </div>
                ))}
                
                {/* Empty cells for start of month */}
                {Array.from({ length: (startOfMonth(historyMonth).getDay() + 6) % 7 }).map((_, i) => (
                  <div key={`empty-${i}`} className="h-14" />
                ))}
                
                {/* Days of month */}
                {eachDayOfInterval({ start: startOfMonth(historyMonth), end: endOfMonth(historyMonth) }).map(day => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const dayEntries = historyEntries.filter(e => e.date === dateStr);
                  const hasEntrada = dayEntries.some(e => e.entry_type === 'entrada');
                  const hasSalida = dayEntries.some(e => e.entry_type === 'salida');
                  const hasIncidencia = dayEntries.some(e => e.entry_type === 'incidencia');
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                  
                  return (
                    <div
                      key={day.toISOString()}
                      className={`
                        h-14 rounded text-sm p-1 border transition-colors
                        ${isToday(day) ? 'ring-2 ring-primary' : ''}
                        ${isWeekend ? 'bg-gray-50' : 'bg-white'}
                        ${dayEntries.length > 0 ? 'border-green-300' : 'border-gray-100'}
                      `}
                    >
                      <div className="font-medium text-xs">{format(day, 'd')}</div>
                      {dayEntries.length > 0 && (
                        <div className="flex flex-wrap gap-0.5 mt-0.5">
                          {hasEntrada && <div className="w-2 h-2 rounded-full bg-green-500" title="Entrada" />}
                          {hasSalida && <div className="w-2 h-2 rounded-full bg-red-500" title="Salida" />}
                          {hasIncidencia && <div className="w-2 h-2 rounded-full bg-fuchsia-500" title="Incidencia" />}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Legend */}
              <div className="flex flex-wrap gap-4 text-xs border-t pt-3">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span>Entrada</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span>Salida</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-fuchsia-500" />
                  <span>Incidencia</span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Entries list for selected month */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Detalle de Fichajes</CardTitle>
            </CardHeader>
            <CardContent>
              {historyEntries.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No hay fichajes en este mes</p>
              ) : (
                <div className="max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Hora</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Notas</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyEntries.map(entry => (
                        <TableRow key={entry.id}>
                          <TableCell className="font-medium">
                            {format(parseISO(entry.date), 'dd/MM/yyyy')}
                          </TableCell>
                          <TableCell className="font-mono">
                            {formatInTimeZone(parseISO(entry.timestamp), MADRID_TZ, 'HH:mm')}
                          </TableCell>
                          <TableCell>
                            <Badge className={ENTRY_BADGES[entry.entry_type]}>
                              {getEntryLabel(entry.entry_type)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {entry.notes || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* VACACIONES TAB */}
        <TabsContent value="vacaciones" className="space-y-4">
          {/* Vacation summary */}
          {vacation && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Mis Vacaciones {vacation.year}</CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const pendingDays = vacationDays.filter(d => d.status === 'pendiente').length;
                  const approvedDays = vacationDays.filter(d => d.status === 'aprobado').length;
                  const rejectedDays = vacationDays.filter(d => d.status === 'rechazado').length;
                  const availableDays = vacation.total_days - approvedDays - pendingDays;
                  const usedPercentage = ((approvedDays + pendingDays) / vacation.total_days) * 100;
                  
                  return (
                    <>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="flex-1">
                          <div className="h-3 bg-gray-200 rounded-full overflow-hidden flex">
                            <div 
                              className="h-full bg-green-500 transition-all"
                              style={{ width: `${(approvedDays / vacation.total_days) * 100}%` }}
                            />
                            <div 
                              className="h-full bg-yellow-400 transition-all"
                              style={{ width: `${(pendingDays / vacation.total_days) * 100}%` }}
                            />
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">{availableDays}</p>
                          <p className="text-xs text-muted-foreground">días disponibles</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center text-sm">
                        <div className="p-2 bg-green-50 rounded">
                          <p className="font-bold text-green-600">{approvedDays}</p>
                          <p className="text-xs text-muted-foreground">Aprobados</p>
                        </div>
                        <div className="p-2 bg-yellow-50 rounded">
                          <p className="font-bold text-yellow-600">{pendingDays}</p>
                          <p className="text-xs text-muted-foreground">Pendientes</p>
                        </div>
                        <div className="p-2 bg-red-50 rounded">
                          <p className="font-bold text-red-600">{rejectedDays}</p>
                          <p className="text-xs text-muted-foreground">Rechazados</p>
                        </div>
                        <div className="p-2 bg-gray-50 rounded">
                          <p className="font-bold text-gray-600">{vacation.total_days}</p>
                          <p className="text-xs text-muted-foreground">Total</p>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          )}
          
          {/* Calendar */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Solicitar Vacaciones</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setVacationMonth(new Date(vacationMonth.setMonth(vacationMonth.getMonth() - 1)))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="font-medium min-w-[140px] text-center">
                    {format(vacationMonth, 'MMMM yyyy', { locale: es })}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => setVacationMonth(new Date(vacationMonth.setMonth(vacationMonth.getMonth() + 1)))}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1">
                {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(day => (
                  <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                    {day}
                  </div>
                ))}
                
                {Array.from({ length: (monthStart.getDay() + 6) % 7 }).map((_, i) => (
                  <div key={`empty-${i}`} className="h-10" />
                ))}
                
                {monthDays.map(day => {
                  const vacDay = isVacationDay(day);
                  const isHoliday = isHolidayDay(day);
                  const isSelected = selectedDates.some(d => isSameDay(d, day));
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                  const isPending = vacDay?.status === 'pendiente';
                  const isApproved = vacDay?.status === 'aprobado';
                  
                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => {
                        if (!isWeekend && !isHoliday && !vacDay) {
                          if (isSelected) {
                            setSelectedDates(prev => prev.filter(d => !isSameDay(d, day)));
                          } else {
                            setSelectedDates(prev => [...prev, day]);
                          }
                        }
                      }}
                      disabled={isWeekend || isHoliday || !!vacDay}
                      className={`
                        h-10 rounded text-sm font-medium transition-colors
                        ${isToday(day) ? 'ring-2 ring-primary' : ''}
                        ${isWeekend ? 'bg-gray-100 text-gray-400' : ''}
                        ${isHoliday ? 'bg-red-100 text-red-800' : ''}
                        ${isApproved ? 'bg-green-200 text-green-800' : ''}
                        ${isPending ? 'bg-yellow-200 text-yellow-800' : ''}
                        ${isSelected ? 'bg-blue-500 text-white' : ''}
                        ${!isWeekend && !isHoliday && !vacDay && !isSelected ? 'hover:bg-gray-100' : ''}
                        disabled:cursor-not-allowed
                      `}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>
              
              <div className="mt-4 flex flex-wrap gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-200 rounded" />
                  <span>Aprobado</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-yellow-200 rounded" />
                  <span>Pendiente</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-red-100 rounded" />
                  <span>Festivo</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-500 rounded" />
                  <span>Seleccionado</span>
                </div>
              </div>
              
              {selectedDates.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg space-y-3">
                  <p className="text-sm font-medium">
                    {selectedDates.length} día(s) seleccionado(s)
                  </p>
                  <Textarea
                    placeholder="Notas para la solicitud (opcional)..."
                    value={vacationNotes}
                    onChange={(e) => setVacationNotes(e.target.value)}
                    rows={2}
                  />
                  <Button onClick={handleRequestVacation} disabled={loading} className="w-full">
                    <Send className="h-4 w-4 mr-2" />
                    Solicitar Vacaciones
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Requested days list */}
          {vacationDays.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Mis Días de Vacaciones</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {vacationDays.map(day => (
                    <Badge 
                      key={day.id} 
                      variant={day.status === 'aprobado' ? 'default' : 'secondary'}
                      className={day.status === 'aprobado' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}
                    >
                      {format(parseISO(day.date), 'dd MMM', { locale: es })}
                      {day.status === 'pendiente' && <Clock className="h-3 w-3 ml-1" />}
                      {day.status === 'aprobado' && <Check className="h-3 w-3 ml-1" />}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* FESTIVOS TAB */}
        <TabsContent value="festivos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Mi Comunidad Autónoma</CardTitle>
              <CardDescription>Selecciona tu comunidad para ver los festivos locales</CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={comunidad || "none"} onValueChange={(v) => handleUpdateComunidad(v === "none" ? "" : v)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Seleccionar comunidad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin comunidad</SelectItem>
                  {COMUNIDADES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
          
          {/* Calendar view for holidays */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Calendario de Festivos</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setHolidayMonth(new Date(holidayMonth.setMonth(holidayMonth.getMonth() - 1)))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="font-medium min-w-[140px] text-center">
                    {format(holidayMonth, 'MMMM yyyy', { locale: es })}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => setHolidayMonth(new Date(holidayMonth.setMonth(holidayMonth.getMonth() + 1)))}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1">
                {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(day => (
                  <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                    {day}
                  </div>
                ))}
                
                {Array.from({ length: (startOfMonth(holidayMonth).getDay() + 6) % 7 }).map((_, i) => (
                  <div key={`empty-${i}`} className="h-12" />
                ))}
                
                {eachDayOfInterval({ start: startOfMonth(holidayMonth), end: endOfMonth(holidayMonth) }).map(day => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const holiday = holidays.find(h => h.date === dateStr);
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                  
                  return (
                    <div
                      key={day.toISOString()}
                      className={`
                        h-12 rounded text-sm p-1 border transition-colors relative
                        ${isToday(day) ? 'ring-2 ring-primary' : ''}
                        ${isWeekend ? 'bg-gray-100 text-gray-400' : 'bg-white'}
                        ${holiday ? (holiday.type === 'nacional' ? 'bg-red-100 border-red-300' : 'bg-blue-100 border-blue-300') : 'border-gray-100'}
                      `}
                      title={holiday?.name || ''}
                    >
                      <div className={`font-medium text-xs ${holiday ? (holiday.type === 'nacional' ? 'text-red-800' : 'text-blue-800') : ''}`}>
                        {format(day, 'd')}
                      </div>
                      {holiday && (
                        <div className="absolute bottom-0.5 left-0.5 right-0.5">
                          <div className={`w-full h-1 rounded ${holiday.type === 'nacional' ? 'bg-red-500' : 'bg-blue-500'}`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-4 flex flex-wrap gap-4 text-xs border-t pt-3">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-red-500 rounded" />
                  <span>Nacional</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-blue-500 rounded" />
                  <span>Local/Regional</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-gray-200 rounded" />
                  <span>Fin de semana</span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Lista de Festivos {holidayMonth.getFullYear()}</CardTitle>
              <CardDescription>
                {holidays.length} festivos ({holidays.filter(h => h.type === 'nacional').length} nacionales
                {comunidad && `, ${holidays.filter(h => h.type === 'local').length} locales de ${comunidad}`})
              </CardDescription>
            </CardHeader>
            <CardContent>
              {holidays.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No hay festivos configurados</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {holidays.map(holiday => (
                    <div key={holiday.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{holiday.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {format(parseISO(holiday.date), 'EEEE, d MMMM', { locale: es })}
                        </p>
                      </div>
                      <Badge className={holiday.type === 'nacional' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}>
                        {holiday.type === 'nacional' ? 'Nacional' : (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {holiday.comunidad}
                          </span>
                        )}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Salida Incidencia — motivo obligatorio */}
      <Dialog open={!!pendingIncidenciaEntry} onOpenChange={(o) => !o && setPendingIncidenciaEntry(null)}>
        <DialogContent className="sm:max-w-md" data-testid="incidencia-reason-dialog">
          <DialogHeader>
            <DialogTitle>Motivo de la salida por incidencia</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Indica brevemente el motivo de la salida. Quedará registrado junto con el fichaje.
            </p>
            <Textarea
              rows={4}
              autoFocus
              placeholder="Ejemplo: incidencia familiar, urgencia médica, corte de luz..."
              value={incidenciaReason}
              onChange={(e) => setIncidenciaReason(e.target.value)}
              data-testid="incidencia-reason-textarea"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingIncidenciaEntry(null)}
              disabled={loading}
              data-testid="incidencia-reason-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={submitIncidenciaReason}
              disabled={loading || !incidenciaReason.trim()}
              className="bg-fuchsia-600 hover:bg-purple-700 text-white"
              data-testid="incidencia-reason-submit"
            >
              Fichar Salida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkerTimeTracking;
